import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Bug, Lightbulb } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const PAGE_COMPONENTS: Record<string, string[]> = {
  'Dashboard': ['Interview Quota', 'Recent Interviews', 'Quick Start'],
  'Interviews': ['Interview Type Selection', 'Interview Setup'],
  'Interview Session': ['AI Interviewer Chat', 'Timer', 'Audio/Video'],
  'Coding Interview': ['Code Editor', 'AI Interviewer Chat', 'Timer', 'Question Panel'],
  'Interview History': ['Interview List', 'Interview Results', 'Feedback'],
  'Role Prep': ['Role Prep List', 'Create New Prep'],
  'Role Prep Detail': ['Custom Interviews', 'Resume Tab', 'Company Research', 'Interview Tips'],
  'Settings': ['Profile Settings', 'Subscription', 'Account'],
  'Recruiter Dashboard': ['Job Listings', 'Candidate Pipeline'],
  'Recruiter Jobs': ['Job Creation', 'Job Details'],
  'Recruiter Talent': ['Talent Search', 'Candidate Profiles'],
  'Recruiter Shortlist': ['Shortlisted Candidates'],
  'Payment/Upgrade': ['Checkout', 'Subscription Management'],
};

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'bug' | 'feature';
  userEmail?: string;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  type,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [page, setPage] = useState('');
  const [component, setComponent] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isBug = type === 'bug';
  const pageRequired = isBug;

  useEffect(() => {
    if (open) {
      setPage('');
      setComponent('');
      setDescription('');
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  useEffect(() => {
    setComponent('');
  }, [page]);

  const availableComponents = page ? PAGE_COMPONENTS[page] || [] : [];

  const canSubmit = description.trim() && (!pageRequired || page) && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${API_URL}/api/feedback`,
        {
          type: isBug ? 'bug' : 'feature',
          page: page || undefined,
          component: component || undefined,
          description: description.trim(),
          email: user?.email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      console.error('Feedback submission error:', err);
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1.5}>
          {isBug ? (
            <Bug size={22} style={{ color: '#DC2626' }} />
          ) : (
            <Lightbulb size={22} style={{ color: '#F59E0B' }} />
          )}
          <Typography variant="h6" component="span">
            {isBug ? 'Report a Bug' : 'Request a Feature'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {isBug ? 'Bug report' : 'Feature request'} submitted successfully. Thank you!
          </Alert>
        ) : (
          <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Page {pageRequired ? '*' : '(optional)'}</InputLabel>
              <Select
                value={page}
                label={`Page ${pageRequired ? '*' : '(optional)'}`}
                onChange={(e) => setPage(e.target.value)}
              >
                {Object.keys(PAGE_COMPONENTS).map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!page || availableComponents.length === 0}>
              <InputLabel>Component (optional)</InputLabel>
              <Select
                value={component}
                label="Component (optional)"
                onChange={(e) => setComponent(e.target.value)}
              >
                {availableComponents.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={isBug ? 'Describe the bug *' : 'Describe your feature request *'}
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isBug
                  ? 'What happened? What did you expect to happen?'
                  : 'What feature would you like to see? How would it help you?'
              }
              fullWidth
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit}
            color={isBug ? 'error' : 'primary'}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
